import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Check, Plus, Trash2, Loader2, Upload, X,
  Image as ImageIcon, Layout, Mail, Phone, MapPin,
  Instagram, Facebook, Twitter, Linkedin, Youtube,
  Newspaper, Copyright, CheckCircle, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import Base from '../Base';

const FooterForm = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [formData, setFormData] = useState({
    brand: {
      name: '',
      description: 'Creating exceptional experiences for our customers.',
      logo: {
        type: 'text',
        text: '',
        imageUrl: ''
      }
    },
    contact: {
      email: '',
      phone: '',
      address: ''
    },
    columns: [
      {
        id: '1',
        title: 'Quick Links',
        links: [
          { label: 'Home', url: '/' },
          { label: 'Shop', url: '/shop' },
          { label: 'About', url: '/about' },
          { label: 'Contact', url: '/contact' }
        ]
      },
      {
        id: '2',
        title: 'Customer Service',
        links: [
          { label: 'Shipping & Returns', url: '/shipping' },
          { label: 'Privacy Policy', url: '/privacy' },
          { label: 'Terms & Conditions', url: '/terms' },
          { label: 'FAQ', url: '/faqs' }
        ]
      }
    ],
    social: {
      facebook: '',
      instagram: '',
      twitter: '',
      linkedin: '',
      youtube: ''
    },
    newsletter: {
      enabled: true,
      title: 'Subscribe to Our Newsletter',
      subtitle: 'Get the latest updates and offers'
    },
    copyright: {
      text: '',
      showPaymentIcons: true
    }
  });

  useEffect(() => {
    loadPageData();
  }, [templateId]);

  const loadPageData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/api/template/${templateId}/page/footer`, {
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      // Try to load from structure.attributes first (new format), fallback to content (old format)
      const attributes = response.data.structure?.attributes || response.data.content;
      if (attributes) {
        setFormData(prev => ({
          ...prev,
          ...attributes,
          brand: { ...prev.brand, ...attributes.brand },
          contact: { ...prev.contact, ...attributes.contact },
          social: { ...prev.social, ...attributes.social },
          newsletter: { ...prev.newsletter, ...attributes.newsletter },
          copyright: { ...prev.copyright, ...attributes.copyright }
        }));
        if (attributes.brand?.logo?.imageUrl) {
          setLogoPreview(attributes.brand.logo.imageUrl);
        }
      }
    } catch (error) {
      console.error('Error loading footer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate(`/template-builder/${templateId}`);
  };

  const handleSave = async (isDraft = true) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');

      const formDataToSend = new FormData();
      formDataToSend.append('status', isDraft ? 'draft' : 'complete');
      formDataToSend.append('content', JSON.stringify(formData));

      if (logoFile) {
        formDataToSend.append('footer_logo_image', logoFile);
      }

      await axios.post(
        `${API_BASE_URL}/api/template/${templateId}/page/footer/save`,
        formDataToSend,
        {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setLastSaved(new Date());
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving:', error);
      setErrorMessage(error.response?.data?.message || 'Error saving changes. Please try again.');
      setShowErrorModal(true);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2);

  const addColumn = () => {
    setFormData(prev => ({
      ...prev,
      columns: [...prev.columns, { id: generateId(), title: '', links: [] }]
    }));
  };

  const updateColumn = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      columns: prev.columns.map((col, i) =>
        i === index ? { ...col, [field]: value } : col
      )
    }));
  };

  const removeColumn = (index) => {
    setFormData(prev => ({
      ...prev,
      columns: prev.columns.filter((_, i) => i !== index)
    }));
  };

  const addLink = (columnIndex) => {
    setFormData(prev => ({
      ...prev,
      columns: prev.columns.map((col, i) =>
        i === columnIndex
          ? { ...col, links: [...col.links, { label: '', url: '' }] }
          : col
      )
    }));
  };

  const updateLink = (columnIndex, linkIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      columns: prev.columns.map((col, i) =>
        i === columnIndex
          ? {
              ...col,
              links: col.links.map((link, j) =>
                j === linkIndex ? { ...link, [field]: value } : link
              )
            }
          : col
      )
    }));
  };

  const removeLink = (columnIndex, linkIndex) => {
    setFormData(prev => ({
      ...prev,
      columns: prev.columns.map((col, i) =>
        i === columnIndex
          ? { ...col, links: col.links.filter((_, j) => j !== linkIndex) }
          : col
      )
    }));
  };

  const addQuickColumn = (suggestion) => {
    setFormData(prev => ({
      ...prev,
      columns: [...prev.columns, { id: generateId(), ...suggestion }]
    }));
  };

  // Success Modal Component - Vasstr colors
  const SuccessModal = () => {
    if (!showSuccessModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={handleSuccessModalClose}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-[#D45614]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#D45614]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Saved Successfully!</h3>
            <p className="text-gray-600 text-sm mb-6">Your footer has been saved.</p>
            <button onClick={handleSuccessModalClose} className="w-full bg-[#D45614] hover:bg-[#b8480f] text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <Check className="w-4 h-4" />
              Continue to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Error Modal Component - Simple design
  const ErrorModal = () => {
    if (!showErrorModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={() => setShowErrorModal(false)}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h3>
            <p className="text-gray-600 text-sm mb-6">{errorMessage}</p>
            <button onClick={() => setShowErrorModal(false)} className="w-full bg-gray-800 hover:bg-gray-900 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <X className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Base Content={
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-[#D45614] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-sm text-gray-500">Loading footer...</p>
          </div>
        </div>
      } />
    );
  }

  const FormContent = (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/template-builder/${templateId}`)}
                className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-500" />
              </button>
              <div>
                <h1 className="text-base font-semibold text-gray-900">Footer</h1>
                {lastSaved && (
                  <p className="text-xs text-gray-400">Saved {lastSaved.toLocaleTimeString()}</p>
                )}
              </div>
            </div>
            <button
              onClick={() => handleSave(false)}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-1.5 bg-[#D45614] text-white text-sm font-medium rounded-lg hover:bg-[#c04d12] disabled:opacity-50 transition-colors"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>Save</span>
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Brand Section */}
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
            <div className="w-9 h-9 bg-[#D45614]/5 rounded-lg flex items-center justify-center">
              <ImageIcon className="w-4 h-4 text-[#D45614]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Brand</h2>
              <p className="text-sm text-gray-500">Your brand identity in footer</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Logo Type</label>
              <div className="flex gap-3">
                <button
                  onClick={() => setFormData(prev => ({ ...prev, brand: { ...prev.brand, logo: { ...prev.brand.logo, type: 'text' } } }))}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 text-sm font-medium transition-all ${
                    formData.brand.logo.type === 'text'
                      ? 'border-[#D45614] bg-[#D45614]/5 text-[#D45614]'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  Text Logo
                </button>
                <button
                  onClick={() => setFormData(prev => ({ ...prev, brand: { ...prev.brand, logo: { ...prev.brand.logo, type: 'image' } } }))}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 text-sm font-medium transition-all ${
                    formData.brand.logo.type === 'image'
                      ? 'border-[#D45614] bg-[#D45614]/5 text-[#D45614]'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  Image Logo
                </button>
              </div>
            </div>

            {formData.brand.logo.type === 'text' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Brand Name</label>
                <input
                  type="text"
                  value={formData.brand.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, brand: { ...prev.brand, name: e.target.value } }))}
                  placeholder="Your Brand Name"
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                />
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Logo Image</label>
                  {logoPreview ? (
                    <div className="relative inline-block">
                      <img src={logoPreview} alt="Logo preview" className="h-16 object-contain border rounded-lg p-2" />
                      <button
                        onClick={() => { setLogoFile(null); setLogoPreview(null); }}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#D45614] hover:bg-[#D45614]/5 transition-colors">
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">Click to upload logo</span>
                      <span className="text-xs text-gray-400 mt-1">PNG, JPG up to 2MB</span>
                      <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                    </label>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Or use URL</label>
                  <input
                    type="url"
                    value={formData.brand.logo.imageUrl}
                    onChange={(e) => { setFormData(prev => ({ ...prev, brand: { ...prev.brand, logo: { ...prev.brand.logo, imageUrl: e.target.value } } })); setLogoPreview(e.target.value); }}
                    placeholder="https://example.com/logo.png"
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Brand Description</label>
              <textarea
                value={formData.brand.description}
                onChange={(e) => setFormData(prev => ({ ...prev, brand: { ...prev.brand, description: e.target.value } }))}
                placeholder="A short description about your brand..."
                rows={3}
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none resize-none"
              />
            </div>
          </div>
        </section>

        {/* Contact Info Section */}
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
            <div className="w-9 h-9 bg-[#D45614]/5 rounded-lg flex items-center justify-center">
              <Mail className="w-4 h-4 text-[#D45614]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Contact Information</h2>
              <p className="text-sm text-gray-500">How customers can reach you</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Mail className="w-4 h-4 inline mr-1.5 text-gray-400" />
                Email
              </label>
              <input
                type="email"
                value={formData.contact.email}
                onChange={(e) => setFormData(prev => ({ ...prev, contact: { ...prev.contact, email: e.target.value } }))}
                placeholder="hello@yourbrand.com"
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Phone className="w-4 h-4 inline mr-1.5 text-gray-400" />
                Phone
              </label>
              <input
                type="tel"
                value={formData.contact.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, contact: { ...prev.contact, phone: e.target.value } }))}
                placeholder="+91 98765 43210"
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <MapPin className="w-4 h-4 inline mr-1.5 text-gray-400" />
                Address
              </label>
              <input
                type="text"
                value={formData.contact.address}
                onChange={(e) => setFormData(prev => ({ ...prev, contact: { ...prev.contact, address: e.target.value } }))}
                placeholder="123 Business Street, Mumbai, India"
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
              />
            </div>
          </div>
        </section>

        {/* Footer Columns Section */}
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between pb-4 mb-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#D45614]/5 rounded-lg flex items-center justify-center">
                <Layout className="w-4 h-4 text-[#D45614]" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Footer Columns</h2>
                <p className="text-sm text-gray-500">Organize links by sections</p>
              </div>
            </div>
            <button
              onClick={addColumn}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-[#D45614] bg-[#D45614]/5 hover:bg-[#D45614]/10 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Column
            </button>
          </div>

          {formData.columns.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <Layout className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500 mb-4">No columns yet</p>
              <div className="flex flex-wrap justify-center gap-2">
                {[
                  { title: 'Quick Links', links: [{ label: 'Home', url: '/' }, { label: 'Shop', url: '/shop' }, { label: 'About', url: '/about' }] },
                  { title: 'Customer Service', links: [{ label: 'Shipping', url: '/shipping' }, { label: 'Returns', url: '/returns' }] },
                  { title: 'Legal', links: [{ label: 'Privacy', url: '/privacy' }, { label: 'Terms', url: '/terms' }] }
                ].map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => addQuickColumn(suggestion)}
                    className="px-3 py-2 text-sm font-medium text-[#D45614] bg-[#D45614]/5 hover:bg-[#D45614]/10 rounded-lg transition-colors"
                  >
                    + {suggestion.title}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {formData.columns.map((column, colIndex) => (
                <div key={column.id || colIndex} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <input
                      type="text"
                      value={column.title}
                      onChange={(e) => updateColumn(colIndex, 'title', e.target.value)}
                      placeholder="Column Title"
                      className="text-sm font-semibold bg-transparent border-none focus:outline-none focus:ring-0 text-gray-900 placeholder-gray-400"
                    />
                    <button
                      onClick={() => removeColumn(colIndex)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="space-y-2">
                    {column.links.map((link, linkIndex) => (
                      <div key={linkIndex} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={link.label}
                          onChange={(e) => updateLink(colIndex, linkIndex, 'label', e.target.value)}
                          placeholder="Label"
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                        />
                        <input
                          type="text"
                          value={link.url}
                          onChange={(e) => updateLink(colIndex, linkIndex, 'url', e.target.value)}
                          placeholder="/url"
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                        />
                        <button
                          onClick={() => removeLink(colIndex, linkIndex)}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => addLink(colIndex)}
                      className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#D45614] hover:bg-[#D45614]/5 rounded-lg transition-colors"
                    >
                      <Plus className="w-3 h-3" />
                      Add Link
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Social Media Section */}
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
            <div className="w-9 h-9 bg-[#D45614]/5 rounded-lg flex items-center justify-center">
              <Instagram className="w-4 h-4 text-[#D45614]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Social Media</h2>
              <p className="text-sm text-gray-500">Connect with your audience</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: 'instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/yourbrand' },
              { key: 'facebook', label: 'Facebook', icon: Facebook, placeholder: 'https://facebook.com/yourbrand' },
              { key: 'twitter', label: 'Twitter/X', icon: Twitter, placeholder: 'https://twitter.com/yourbrand' },
              { key: 'linkedin', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/company/yourbrand' },
              { key: 'youtube', label: 'YouTube', icon: Youtube, placeholder: 'https://youtube.com/@yourbrand' }
            ].map(({ key, label, icon: Icon, placeholder }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <Icon className="w-4 h-4 inline mr-1.5 text-gray-400" />
                  {label}
                </label>
                <input
                  type="url"
                  value={formData.social[key]}
                  onChange={(e) => setFormData(prev => ({ ...prev, social: { ...prev.social, [key]: e.target.value } }))}
                  placeholder={placeholder}
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                />
              </div>
            ))}
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between pb-4 mb-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#D45614]/5 rounded-lg flex items-center justify-center">
                <Newspaper className="w-4 h-4 text-[#D45614]" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Newsletter</h2>
                <p className="text-sm text-gray-500">Signup section in footer</p>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.newsletter.enabled}
                onChange={(e) => setFormData(prev => ({ ...prev, newsletter: { ...prev.newsletter, enabled: e.target.checked } }))}
                className="w-4 h-4 text-[#D45614] border-gray-300 rounded focus:ring-[#D45614]"
              />
              <span className="text-sm text-gray-600">Enable</span>
            </label>
          </div>

          {formData.newsletter.enabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Title</label>
                <input
                  type="text"
                  value={formData.newsletter.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, newsletter: { ...prev.newsletter, title: e.target.value } }))}
                  placeholder="Subscribe to Our Newsletter"
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Subtitle</label>
                <input
                  type="text"
                  value={formData.newsletter.subtitle}
                  onChange={(e) => setFormData(prev => ({ ...prev, newsletter: { ...prev.newsletter, subtitle: e.target.value } }))}
                  placeholder="Get the latest updates"
                  className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
                />
              </div>
            </div>
          )}
        </section>

        {/* Copyright Section */}
        <section className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 pb-4 mb-5 border-b border-gray-100">
            <div className="w-9 h-9 bg-[#D45614]/5 rounded-lg flex items-center justify-center">
              <Copyright className="w-4 h-4 text-[#D45614]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Copyright</h2>
              <p className="text-sm text-gray-500">Bottom bar content</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Copyright Text</label>
              <input
                type="text"
                value={formData.copyright.text}
                onChange={(e) => setFormData(prev => ({ ...prev, copyright: { ...prev.copyright, text: e.target.value } }))}
                placeholder={`© ${new Date().getFullYear()} Your Brand. All rights reserved.`}
                className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:border-[#D45614] focus:ring-2 focus:ring-[#D45614]/10 focus:outline-none"
              />
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.copyright.showPaymentIcons}
                onChange={(e) => setFormData(prev => ({ ...prev, copyright: { ...prev.copyright, showPaymentIcons: e.target.checked } }))}
                className="w-4 h-4 text-[#D45614] border-gray-300 rounded focus:ring-[#D45614]"
              />
              <span className="text-sm text-gray-700">Show Payment Method Icons</span>
            </label>
          </div>
        </section>
      </main>

      {/* Modals */}
      <SuccessModal />
      <ErrorModal />
    </div>
  );

  return <Base Content={FormContent} />;
};

export default FooterForm;
